import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { CredentialType } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { Subject, takeUntil } from 'rxjs';
import { KeycloakCustomService } from '../../../../lib/services/keycloak-wrapper-service/keycloak-custom.service';
import { ConfirmationRequest } from '../../../../lib/services/keycloak-wrapper-service/keycloak-custom.type';
import { LocaleVariableState } from '../../../../lib/state/locale-variable.state';
import { getConfirmationLocaleVariant } from '../utils/get-confirmation-locale-values';

/** Dialog context passed by OverlayConfirmationService via BrnDialogService.open. */
export interface OverlayConfirmationContext {
  /** Keycloak confirmation claim being confirmed (e.g. ConfirmationClaimType.MAKE_PAYMENT). */
  claimType: string;
  /** Credential type resolved from the confirmation preference order. */
  credentialType: CredentialType;
  /** Optional human-readable reason shown under the title (e.g. the gated operation). */
  reason?: string;
}

/** Result the dialog closes with; mapped by OverlayConfirmationService. */
export type OverlayConfirmationResult = { token: string } | { declined: 'cancelled' | 'timeout' };

/** Mirrors ConfirmationCodeComponent's 2-minute auto-cancel. */
export const OVERLAY_CONFIRMATION_TIMEOUT_MS = 120000;

/**
 * Overlay-hosted variant of ConfirmationCodeComponent
 * (../confirmation-code-component/confirmation-code.component.ts). Renders the
 * same confirmation-code UX (masked destination subtitle, code entry, resend —
 * all via the shared PinBoxesComponent) inside a BrnDialog above the current
 * page, so the host page (and any custom-widget iframe on it) survives the
 * confirmation. The submit flow intentionally duplicates the routed
 * component's onSubmit/handleConfirmation minus all router/location side
 * effects: instead of navigating on success/back it closes the dialog with an
 * OverlayConfirmationResult.
 */
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-overlay-confirmation',
  templateUrl: './overlay-confirmation.component.html',
  styleUrl: './overlay-confirmation.component.scss',
})
export class OverlayConfirmationComponent implements OnInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  private readonly dialogRef = inject<BrnDialogRef<OverlayConfirmationResult>>(BrnDialogRef);
  private readonly context = injectBrnDialogContext<OverlayConfirmationContext>();
  private readonly store = inject(Store);
  private readonly keycloakService = inject(KeycloakCustomService);

  public pin = '';
  public error = '';
  public readonly MAX_LENGTH: number = 6;
  public confirmation: CredentialType;
  public reason?: string;
  public localeValues?: ReturnType<typeof getConfirmationLocaleVariant>;
  private timeoutRef: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    // Biometric challenges need the flutter webview bridge and are not
    // available in the overlay; fall back to PIN entry exactly as
    // ConfirmationCodeComponent.onSubmit does after a failed biometric.
    this.confirmation =
      this.context.credentialType === CredentialType.BIOMETRIC
        ? CredentialType.PIN
        : this.context.credentialType;
    this.reason = this.context.reason;

    this.store
      .select(LocaleVariableState.getLocaleContent)
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => {
        this.localeValues = getConfirmationLocaleVariant(content, this.confirmation, false);
      });

    // Auto-decline after 2 minutes, mirroring ConfirmationCodeComponent's
    // timeout (which does location.back(); here decline must be explicit).
    this.timeoutRef = setTimeout(() => {
      this.dialogRef.close({ declined: 'timeout' });
    }, OVERLAY_CONFIRMATION_TIMEOUT_MS);
  }

  ngOnDestroy(): void {
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
    }
  }

  public checkPinComplete(): void {
    if (this.pin.length === this.MAX_LENGTH && /^\d+$/.test(this.pin)) {
      setTimeout(() => this.onSubmit(), 200);
    }
  }

  public async onSubmit(): Promise<void> {
    try {
      const payload: ConfirmationRequest = {
        claim: this.context.claimType,
        factors: [
          {
            type: this.confirmation,
            value: this.pin,
          },
        ],
      };
      const { access_token } = await this.keycloakService.sendConfirmation(payload);
      this.dialogRef.close({ token: access_token });
    } catch (error) {
      console.error('Error during confirmation:', error);
      // Same placeholder copy as ConfirmationCodeComponent.onSubmit.
      this.error = 'Invalid passcode, please try again';
      this.pin = '';
    }
  }

  public onCancel(): void {
    this.dialogRef.close({ declined: 'cancelled' });
  }
}
